import React, { useEffect, useState, useCallback, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../utils/api";

// Helper to parse cabin group string into cabin name and room name
const parseCabinGroup = (cabinGroupString) => {
  if (!cabinGroupString) return { cabin: "", room: "" };
  const parts = cabinGroupString.split(" | ");
  if (parts.length === 2) {
    return { cabin: parts[0].strip ? parts[0].strip() : parts[0].trim(), room: parts[1].strip ? parts[1].strip() : parts[1].trim() };
  }
  return { cabin: cabinGroupString.trim(), room: "General" };
};

// Helper to normalize room data object
const normalizeRoom = (r) => {
  if (typeof r === "string") {
    return {
      name: r,
      max_occupancy: 4,
      location: "Single Level",
      handicap_accessible: false,
      beds: { king: 0, queen: 0, full: 0, twin: 0, bunk: 0 }
    };
  }
  return {
    name: r.name || "",
    max_occupancy: r.max_occupancy !== undefined ? parseInt(r.max_occupancy) : 4,
    location: r.location || "Single Level",
    handicap_accessible: r.handicap_accessible || false,
    beds: {
      king: r.beds?.king !== undefined ? parseInt(r.beds.king) : 0,
      queen: r.beds?.queen !== undefined ? parseInt(r.beds.queen) : 0,
      full: r.beds?.full !== undefined ? parseInt(r.beds.full) : 0,
      twin: r.beds?.twin !== undefined ? parseInt(r.beds.twin) : 0,
      bunk: r.beds?.bunk !== undefined ? parseInt(r.beds.bunk) : 0
    }
  };
};

// Dropdown for assigning campers to a cabin/room without drag-and-drop.
// Defined at module scope (not inside CabinsPage) so its identity stays
// stable across renders - previously it was declared inside the page
// component, which meant React tore down and rebuilt this <select> from
// scratch on every single re-render (search typing, drag-hover, etc.),
// which is what showed up as lag/jank when using the dropdown.
const CabinRoomSelector = React.memo(function CabinRoomSelector({ camperIds, currentCabin, currentRoom, isAdmin, cabinsConfig, onAssign }) {
  if (!isAdmin) return null;
  return (
    <select
      className="form-select selector-box"
      value=""
      onChange={(e) => {
        const val = e.target.value;
        if (val) {
          const [cab, rm] = val.split(" | ");
          onAssign(camperIds, cab, rm);
        }
      }}
    >
      <option value="" disabled>Move occupants to...</option>
      {cabinsConfig.map(cb => (
        <optgroup key={cb.name} label={cb.name}>
          {cb.rooms.map(rm => {
            const rmName = typeof rm === "string" ? rm : rm.name;
            if (cb.name === currentCabin && rmName === currentRoom) return null;
            return (
              <option key={`${cb.name} | ${rmName}`} value={`${cb.name} | ${rmName}`}>
                {cb.name} — {rmName}
              </option>
            );
          })}
        </optgroup>
      ))}
    </select>
  );
});

export default function CabinsPage() {
  const { user, hasPermission } = useAuth();
  const isAdmin = hasPermission("cabins", "edit");
  const canExport = user?.role === "owner" || user?.role === "admin";
  const isOwner = user?.role === "owner";
  
  // Campers data state
  const [campers, setCampers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState(null); // { type: "success"|"error", text }
  const [editingRoom, setEditingRoom] = useState(null); // { cabinName, room }
  const [addingRoomToCabin, setAddingRoomToCabin] = useState(null); // cabinName
  const [newRoomForm, setNewRoomForm] = useState({
    name: "",
    max_occupancy: 4,
    location: "Single Level",
    handicap_accessible: false,
    beds: { king: 0, queen: 0, full: 0, twin: 0, bunk: 0 }
  });

  const handleExportExcel = () => {
    if (!canExport) return;
    const headers = [
      "First Name",
      "Last Name",
      "Cabin Name",
      "Room Number",
      "Family Group"
    ];

    const rows = campers.map(c => {
      const { cabin, room } = parseCabinGroup(c.cabin_group);
      return [
        `"${(c.first_name || "").replace(/"/g, '""')}"`,
        `"${(c.last_name || "").replace(/"/g, '""')}"`,
        `"${cabin.replace(/"/g, '""')}"`,
        `"${room.replace(/"/g, '""')}"`,
        `"${(c.family_group || "").replace(/"/g, '""')}"`
      ];
    });

    const csvContent = "\uFEFF" + [headers.join(","), ...rows.map(e => e.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `gca_cabin_details_export_${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // UI filter state
  const [search, setSearch] = useState("");
  const [newCabinName, setNewCabinName] = useState("");
  
  // Cabins config: loaded from localStorage, or defaults, merged on data fetch
  const [cabinsConfig, setCabinsConfig] = useState(() => {
    // Run a one-time reset check to clear previous defaults
    const isReset = localStorage.getItem("gca_cabins_reset_v4");
    if (!isReset) {
      localStorage.removeItem("gca_cabins_config");
      localStorage.setItem("gca_cabins_reset_v4", "true");
    }

    const saved = localStorage.getItem("gca_cabins_config");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return parsed.map(c => ({
          ...c,
          rooms: (c.rooms || []).map(normalizeRoom)
        }));
      } catch (e) {
        // Fallback below
      }
    }
    return [];
  });

  const [dragOverBox, setDragOverBox] = useState(null); // e.g., "Cabin A | Room 1" - which room the pointer is currently over
  const [draggingKey, setDraggingKey] = useState(null); // key of the origin card being dragged (dims it)
  const [dragGhost, setDragGhost] = useState(null); // { x, y, label } or null when not dragging
  const dragCtxRef = useRef(null); // mutable drag session data - avoids a re-render per pointermove sample
  const dragGhostRef = useRef(null); // direct DOM ref for 60fps ghost position updates (avoids React re-render per frame)
  const [showVacantRooms, setShowVacantRooms] = useState(false);
  const [highlightedBoxId, setHighlightedBoxId] = useState(null);

  // Stable DOM id for a room's card, so the vacant-rooms panel can jump to it.
  const roomAnchorId = (cabinName, roomName) =>
    `room-anchor-${cabinName}-${roomName}`.replace(/[^a-zA-Z0-9_-]+/g, "-");

  const jumpToRoom = (cabinName, roomName) => {
    const el = document.getElementById(roomAnchorId(cabinName, roomName));
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    const boxId = `${cabinName} | ${roomName}`;
    setHighlightedBoxId(boxId);
    setTimeout(() => setHighlightedBoxId(prev => (prev === boxId ? null : prev)), 2200);
  };

  // Success/error flash helper
  const flash = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 4000);
  };

  const saveConfig = (newConfig) => {
    setCabinsConfig(newConfig);
    localStorage.setItem("gca_cabins_config", JSON.stringify(newConfig));
    api.post("/api/settings/", { cabins_config: JSON.stringify(newConfig) })
      .catch(err => console.error("Failed to save cabins config to database settings:", err));
  };

  useEffect(() => {
    api.get("/api/settings/")
      .then(res => {
        const dbConfigStr = res.data.settings?.cabins_config;
        const localSaved = localStorage.getItem("gca_cabins_config");
        
        let localParsed = [];
        if (localSaved) {
          try {
            localParsed = JSON.parse(localSaved);
          } catch (e) {}
        }

        if (dbConfigStr && dbConfigStr !== "[]") {
          try {
            const parsed = JSON.parse(dbConfigStr);
            const normalized = parsed.map(c => ({
              ...c,
              rooms: (c.rooms || []).map(normalizeRoom)
            }));
            setCabinsConfig(normalized);
            localStorage.setItem("gca_cabins_config", JSON.stringify(normalized));
          } catch (e) {
            console.error("Failed to parse cabins config from DB settings:", e);
          }
        } else if (localParsed && localParsed.length > 0) {
          // DB has no config but local has it -> upload local to DB!
          api.post("/api/settings/", { cabins_config: JSON.stringify(localParsed) })
            .catch(err => console.error("Failed to sync local cabins config to DB:", err));
        }
      })
      .catch(err => console.error("Failed to load cabins config from DB settings:", err));
  }, []);

  // Fetch campers and merge any active cabin/room mappings dynamically
  const fetchCampers = useCallback(() => {
    setLoading(true);
    api.get("/api/campers/?per_page=1000&status=registered")
      .then(r => {
        const camperList = r.data.campers;
        setCampers(camperList);

        // Merge any cabin/room values from DB into config if they are missing
        setCabinsConfig(prev => {
          let updated = [...prev];
          camperList.forEach(c => {
            if (c.cabin_group) {
              const { cabin, room } = parseCabinGroup(c.cabin_group);
              if (cabin && room) {
                // Find or add cabin
                let cab = updated.find(cb => cb.name.toLowerCase() === cabin.toLowerCase());
                if (!cab) {
                  cab = { name: cabin, rooms: [normalizeRoom(room)] };
                  updated.push(cab);
                } else {
                  const roomNames = cab.rooms.map(rm => typeof rm === "string" ? rm.toLowerCase() : rm.name.toLowerCase());
                  if (!roomNames.includes(room.toLowerCase())) {
                    cab.rooms.push(normalizeRoom(room));
                  }
                }
              }
            }
          });

          // Sort cabins and rooms alphabetically/numerically
          updated = updated.map(cb => ({
            ...cb,
            rooms: [...cb.rooms].map(normalizeRoom).sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }))
          })).sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }));

          localStorage.setItem("gca_cabins_config", JSON.stringify(updated));
          return updated;
        });
      })
      .catch(() => setError("Failed to load campers."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchCampers();
  }, [fetchCampers]);

  // Handle generating and printing PDF report
  const handlePrintPDF = async () => {
    try {
      flash("info", "Generating cabin assignments PDF report...");
      const response = await api.get("/api/campers/cabins-pdf", {
        responseType: "blob",
      });
      
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "gca_camp_cabins_report.pdf");
      document.body.appendChild(link);
      link.click();
      
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      flash("success", "PDF report downloaded successfully!");
    } catch (err) {
      console.error(err);
      flash("error", "Failed to generate PDF report.");
    }
  };

  // Handle assigning camper to cabin/room
  const assignCabin = async (camperId, cabinName, roomName) => {
    const camper = campers.find(c => c.id === camperId);
    if (!camper) return;

    const previousCabinGroup = camper.cabin_group;
    const targetValue = cabinName && roomName ? `${cabinName} | ${roomName}` : "";

    // Optimistically update local state
    setCampers(prev => prev.map(c => {
      if (c.id === camperId) {
        return { ...c, cabin_group: targetValue };
      }
      return c;
    }));

    try {
      await api.put(`/api/campers/${camperId}`, { cabin_group: targetValue });
      if (targetValue) {
        flash("success", `Moved ${camper.first_name} ${camper.last_name} to ${cabinName} - ${roomName}.`);
      } else {
        flash("success", `Unassigned ${camper.first_name} ${camper.last_name}.`);
      }
    } catch (err) {
      // Revert state
      setCampers(prev => prev.map(c => {
        if (c.id === camperId) {
          return { ...c, cabin_group: previousCabinGroup };
        }
        return c;
      }));
      flash("error", err.response?.data?.error || "Failed to update assignment.");
    }
  };

  // Assign multiple family members to the same cabin room
  const assignFamily = async (camperIds, cabinName, roomName) => {
    if (!camperIds.length) return;
    const targetValue = cabinName && roomName ? `${cabinName} | ${roomName}` : "";
    
    // Save original state of family members for rollback
    const originalState = campers.filter(c => camperIds.includes(c.id));

    // Optimistically update
    setCampers(prev => prev.map(c => {
      if (camperIds.includes(c.id)) {
        return { ...c, cabin_group: targetValue };
      }
      return c;
    }));

    try {
      await Promise.all(camperIds.map(id => api.put(`/api/campers/${id}`, { cabin_group: targetValue })));
      if (targetValue) {
        flash("success", `Assigned family members to ${cabinName} - ${roomName}.`);
      } else {
        flash("success", `Unassigned family members.`);
      }
    } catch (err) {
      // Rollback
      setCampers(prev => prev.map(c => {
        const orig = originalState.find(o => o.id === c.id);
        if (orig) {
          return { ...c, cabin_group: orig.cabin_group };
        }
        return c;
      }));
      flash("error", "Failed to assign family group.");
    }
  };

  // Shared onAssign handler passed to every CabinRoomSelector instance.
  // Deliberately not wrapped in useCallback: assignCabin/assignFamily are
  // redefined each render closing over the current `campers` state, and
  // memoizing this with an empty dep array would permanently capture the
  // first render's (stale) versions of them.
  const handleSelectorAssign = (camperIds, cabinName, roomName) => {
    if (camperIds.length === 1) assignCabin(camperIds[0], cabinName, roomName);
    else assignFamily(camperIds, cabinName, roomName);
  };

  // Unassign all campers in a specific room
  const clearRoom = async (cabinName, roomName) => {
    const roomKey = `${cabinName} | ${roomName}`;
    const roomCampers = campers.filter(c => c.cabin_group === roomKey);
    const camperIds = roomCampers.map(c => c.id);
    
    if (camperIds.length === 0) return;
    
    if (!await window.confirm(`Are you sure you want to unassign all ${camperIds.length} campers from ${cabinName} - ${roomName}?`)) {
      return;
    }
    
    const originalState = [...campers];
    
    // Optimistically update
    setCampers(prev => prev.map(c => {
      if (camperIds.includes(c.id)) {
        return { ...c, cabin_group: "" };
      }
      return c;
    }));
    
    try {
      flash("info", `Clearing room ${roomName}...`);
      await Promise.all(camperIds.map(id => api.put(`/api/campers/${id}`, { cabin_group: "" })));
      flash("success", `Successfully unassigned all campers from ${cabinName} - ${roomName}.`);
    } catch (err) {
      // Rollback
      setCampers(originalState);
      flash("error", "Failed to clear room assignments.");
    }
  };

  // Add a new cabin
  const handleAddCabin = (e) => {
    e.preventDefault();
    const name = newCabinName.trim();
    if (!name) return;
    if (cabinsConfig.some(c => c.name.toLowerCase() === name.toLowerCase())) {
      flash("error", "Cabin name already exists.");
      return;
    }
    const newConfig = [...cabinsConfig, { name, rooms: [] }].sort((a, b) => 
      a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' })
    );
    saveConfig(newConfig);
    setNewCabinName("");
    flash("success", `Created cabin "${name}".`);
  };

  // Delete a cabin (only if all rooms inside are empty)
  const handleDeleteCabin = async (cabinName) => {
    const cabinCampers = campers.filter(c => {
      const { cabin } = parseCabinGroup(c.cabin_group);
      return cabin.toLowerCase() === cabinName.toLowerCase();
    });

    if (cabinCampers.length > 0) {
      await alert("Cannot delete cabin. Make sure all rooms inside this cabin are empty first!");
      return;
    }

    const newConfig = cabinsConfig.filter(c => c.name.toLowerCase() !== cabinName.toLowerCase());
    saveConfig(newConfig);
    flash("success", `Deleted empty cabin "${cabinName}".`);
  };

  const handleImportExcel = async (e) => {
    if (!isOwner) {
      await alert("Only owners are allowed to import cabins Excel sheets.");
      e.target.value = null;
      return;
    }
    const file = e.target.files[0];
    if (!file) return;

    if (!await window.confirm("Importing an Excel sheet will overwrite any duplicate cabins/rooms. Do you want to proceed?")) {
      e.target.value = null;
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    flash("info", "Processing Excel file...");
    try {
      const res = await api.post("/api/campers/upload-cabins", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      const parsedConfig = res.data.config;
      let merged = [...cabinsConfig];
      
      parsedConfig.forEach(parsedCab => {
        let existingCab = merged.find(c => c.name.toLowerCase() === parsedCab.name.toLowerCase());
        if (!existingCab) {
          merged.push(parsedCab);
        } else {
          parsedCab.rooms.forEach(parsedRm => {
            let existingRmIdx = existingCab.rooms.findIndex(r => {
              const name = typeof r === "string" ? r : r.name;
              return name.toLowerCase() === parsedRm.name.toLowerCase();
            });
            if (existingRmIdx === -1) {
              existingCab.rooms.push(parsedRm);
            } else {
              existingCab.rooms[existingRmIdx] = parsedRm;
            }
          });
          
          existingCab.rooms.sort((a, b) => {
            const nameA = typeof a === "string" ? a : a.name;
            const nameB = typeof b === "string" ? b : b.name;
            return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
          });
        }
      });
      
      merged.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }));

      saveConfig(merged);
      flash("success", `Successfully imported cabins from Excel!`);
    } catch (err) {
      await alert(err.response?.data?.error || "Failed to process Excel sheet.");
    } finally {
      e.target.value = null;
    }
  };

  // Add room to cabin
  const handleAddRoom = (cabinName) => {
    setNewRoomForm({
      name: "",
      max_occupancy: 4,
      location: "Single Level",
      handicap_accessible: false,
      beds: { king: 0, queen: 0, full: 0, twin: 0, bunk: 0 }
    });
    setAddingRoomToCabin(cabinName);
  };

  // Delete room from cabin (only if empty)
  const handleDeleteRoom = async (cabinName, roomName) => {
    const roomCampers = campers.filter(c => {
      const { cabin, room } = parseCabinGroup(c.cabin_group);
      return cabin.toLowerCase() === cabinName.toLowerCase() && room.toLowerCase() === roomName.toLowerCase();
    });

    if (roomCampers.length > 0) {
      await alert("Cannot delete room while it has campers assigned to it.");
      return;
    }

    const newConfig = cabinsConfig.map(c => {
      if (c.name.toLowerCase() === cabinName.toLowerCase()) {
        return { ...c, rooms: c.rooms.filter(r => (typeof r === "string" ? r.toLowerCase() : r.name.toLowerCase()) !== roomName.toLowerCase()) };
      }
      return c;
    });

    saveConfig(newConfig);
    flash("success", `Deleted empty room "${roomName}" from ${cabinName}.`);
  };

  // Drag and drop, built on Pointer Events for real 1:1 tracking (the
  // dragged card follows the pointer from the exact point it was grabbed,
  // not snapped to its center) instead of native HTML5 drag-and-drop, which
  // offsets to a fixed drag image and has inconsistent dragover/dragleave
  // firing across browsers.

  const prefersReducedMotion = () =>
    typeof window !== "undefined" && window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Hit-test the element under the pointer without relying on elementFromPoint
  // alone: on older iOS Safari, pointer capture causes elementFromPoint to
  // return the capturing element rather than the topmost element at those
  // coordinates, so we fall back to a manual bounding-rect scan if the
  // primary result looks like the capturing card.
  const findRoomBoxAt = (clientX, clientY, capturedEl) => {
    const el = document.elementFromPoint(clientX, clientY);
    const box = el ? el.closest(".room-box") : null;
    if (box) return box;
    if (el && capturedEl && (el === capturedEl || capturedEl.contains(el))) {
      return Array.from(document.querySelectorAll(".room-box")).find(rb => {
        const r = rb.getBoundingClientRect();
        return clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom;
      }) || null;
    }
    return null;
  };

  // Small dependency-free spring: critically damped by default (settling
  // into a room shouldn't overshoot), a touch of bounce only for the
  // invalid-drop return since that's the one case actually carrying the
  // gesture's momentum. Decomposed into independent X/Y terms in the same
  // step loop, per "decompose 2D motion into independent springs".
  // Ghost position is written directly to the DOM node (dragGhostRef) to
  // avoid scheduling a React state update on every rAF frame.
  const springGhostTo = ({ toX, toY, velocityX = 0, velocityY = 0, bounce, onDone }) => {
    const ctx = dragCtxRef.current;
    if (!ctx) return;

    const applyPos = (x, y) => {
      if (dragGhostRef.current) dragGhostRef.current.style.transform = `translate(${x}px, ${y}px)`;
    };

    // Guard: only fire onDone if this spring's ctx is still the active drag.
    // A new startDrag overwrites dragCtxRef.current, making the old ctx stale.
    const settle = () => { if (dragCtxRef.current === ctx) onDone && onDone(); };

    if (prefersReducedMotion()) {
      ctx.currentX = toX;
      ctx.currentY = toY;
      applyPos(toX, toY);
      settle();
      return;
    }

    let x = ctx.currentX;
    let y = ctx.currentY;
    let vx = velocityX;
    let vy = velocityY;
    let lastT = performance.now();
    const stiffness = 210;
    const damping = bounce ? 20 : 28;

    const step = (now) => {
      const dt = Math.min((now - lastT) / 1000, 0.032);
      lastT = now;

      vx += (-stiffness * (x - toX) - damping * vx) * dt;
      vy += (-stiffness * (y - toY) - damping * vy) * dt;
      x += vx * dt;
      y += vy * dt;

      ctx.currentX = x;
      ctx.currentY = y;
      applyPos(x, y);

      if (Math.hypot(x - toX, y - toY) < 0.75 && Math.hypot(vx, vy) < 40) {
        ctx.currentX = toX;
        ctx.currentY = toY;
        applyPos(toX, toY);
        settle();
        return;
      }
      ctx.springFrame = requestAnimationFrame(step);
    };
    ctx.springFrame = requestAnimationFrame(step);
    ctx.cancelSpring = () => cancelAnimationFrame(ctx.springFrame);
  };

  const startDrag = (e, dragType, data, key, label) => {
    if (!isAdmin) return;
    // Clicking the assign dropdown (or any button) inside the card shouldn't
    // start a drag.
    if (e.target.closest("select, button, .selector-box")) return;
    if (e.button !== undefined && e.button !== 0) return;

    // Prevent the browser from synthesizing mouse/click events after this
    // pointer sequence, so finger drift onto a button child doesn't fire its onClick.
    e.preventDefault();

    // If a previous drag's settle/return spring is still animating, cancel
    // it before starting a new one - two drags must never fight over the
    // same ghost state (never lock out input during a transition).
    if (dragCtxRef.current?.cancelSpring) dragCtxRef.current.cancelSpring();

    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    card.setPointerCapture(e.pointerId);

    dragCtxRef.current = {
      payload: { type: dragType, ...data },
      originX: rect.left,
      originY: rect.top,
      currentX: rect.left,
      currentY: rect.top,
      grabOffsetX: e.clientX - rect.left,
      grabOffsetY: e.clientY - rect.top,
      history: [{ x: e.clientX, y: e.clientY, t: performance.now() }],
      moved: false,
      cancelSpring: null,
      springFrame: null
    };

    setDraggingKey(key);
    setDragGhost({ x: rect.left, y: rect.top, label });
  };

  const onDragPointerMove = (e) => {
    const ctx = dragCtxRef.current;
    if (!ctx) return;

    // ~10px hysteresis before committing to a drag, per the gesture-design
    // checklist - avoids treating a slightly-shaky click as a drag.
    if (!ctx.moved) {
      const first = ctx.history[0];
      if (Math.hypot(e.clientX - first.x, e.clientY - first.y) > 8) ctx.moved = true;
    }

    const x = e.clientX - ctx.grabOffsetX;
    const y = e.clientY - ctx.grabOffsetY;
    ctx.currentX = x;
    ctx.currentY = y;

    ctx.history.push({ x: e.clientX, y: e.clientY, t: performance.now() });
    if (ctx.history.length > 5) ctx.history.shift();

    // Write position directly to the DOM node — skips React reconciliation
    // at 60fps; setDragGhost is only called to show/hide the ghost.
    if (dragGhostRef.current) dragGhostRef.current.style.transform = `translate(${x}px, ${y}px)`;

    const roomBox = findRoomBoxAt(e.clientX, e.clientY, e.currentTarget);
    const overKey = roomBox ? roomBox.dataset.boxKey : null;
    setDragOverBox(prev => (prev === overKey ? prev : overKey));
  };

  const onDragPointerUp = async (e) => {
    const ctx = dragCtxRef.current;
    if (!ctx) return;
    e.currentTarget.releasePointerCapture(e.pointerId);
    setDragOverBox(null);

    if (!ctx.moved) {
      // Just a press-and-release with no real movement - nothing to animate.
      setDragGhost(null);
      setDraggingKey(null);
      dragCtxRef.current = null;
      return;
    }

    // Release velocity from the last couple of samples, handed off to the
    // spring so there's no seam between dragging and animating.
    const hist = ctx.history;
    let vx = 0, vy = 0;
    if (hist.length >= 2) {
      const a = hist[hist.length - 2];
      const b = hist[hist.length - 1];
      const dt = Math.max((b.t - a.t) / 1000, 0.001);
      vx = (b.x - a.x) / dt;
      vy = (b.y - a.y) / dt;
    }

    const roomBox = findRoomBoxAt(e.clientX, e.clientY, e.currentTarget);

    if (roomBox) {
      const targetCabin = roomBox.dataset.cabinName;
      const targetRoom = roomBox.dataset.roomName;
      const targetRect = roomBox.getBoundingClientRect();

      springGhostTo({
        // Approximate half-width/height of the compact ghost pill - exact
        // centering isn't critical here, it's a settle cue, not a layout.
        toX: targetRect.left + targetRect.width / 2 - 60,
        toY: targetRect.top + targetRect.height / 2 - 16,
        velocityX: vx, velocityY: vy,
        bounce: false,
        onDone: () => { setDragGhost(null); setDraggingKey(null); dragCtxRef.current = null; }
      });

      try {
        if (ctx.payload.type === "single") {
          await assignCabin(ctx.payload.ids[0], targetCabin, targetRoom);
        } else if (ctx.payload.type === "family") {
          await assignFamily(ctx.payload.ids, targetCabin, targetRoom);
        }
      } catch (err) {
        console.error("Drop operation failed", err);
      }
    } else {
      // Dropped somewhere with no room underneath - spring back to origin.
      try {
        springGhostTo({
          toX: ctx.originX, toY: ctx.originY,
          velocityX: vx, velocityY: vy,
          bounce: true,
          onDone: () => { setDragGhost(null); setDraggingKey(null); dragCtxRef.current = null; }
        });
      } catch {
        setDragGhost(null);
        setDraggingKey(null);
        dragCtxRef.current = null;
      }
    }
  };

  // Search filtering
  const filteredCampers = (() => {
    if (!search.trim()) return campers;
    const query = search.toLowerCase();
    const matchingFamilyGroups = new Set(
      campers
        .filter(c => 
          c.first_name.toLowerCase().includes(query) ||
          c.last_name.toLowerCase().includes(query) ||
          (c.family_group && c.family_group.toLowerCase().includes(query))
        )
        .map(c => c.family_group)
        .filter(Boolean)
    );
    return campers.filter(c => {
      const matchesSearch = 
        c.first_name.toLowerCase().includes(query) ||
        c.last_name.toLowerCase().includes(query) ||
        (c.family_group && c.family_group.toLowerCase().includes(query));
      const belongsToMatchingFamily = c.family_group && matchingFamilyGroups.has(c.family_group);
      return matchesSearch || belongsToMatchingFamily;
    });
  })();

  // Extract unassigned campers and group them by Family Group
  const unassignedCampers = filteredCampers.filter(c => !c.cabin_group);
  const unassignedList = [];
  const groupedIds = new Set();

  unassignedCampers.forEach(c => {
    if (groupedIds.has(c.id)) return;

    if (c.family_group) {
      const familyMembers = unassignedCampers.filter(m => m.family_group === c.family_group);
      familyMembers.forEach(m => groupedIds.add(m.id));
      unassignedList.push({
        type: "family",
        id: c.family_group,
        familyName: familyMembers.find(m => m.guardian_name && m.guardian_name !== "Self")?.last_name || c.last_name,
        members: familyMembers
      });
    } else {
      groupedIds.add(c.id);
      unassignedList.push({
        type: "single",
        id: c.id,
        camper: c
      });
    }
  });

  // Retrieve assigned campers for a specific room
  const getRoomOccupants = (cabinName, roomName) => {
    return campers.filter(c => {
      if (!c.cabin_group) return false;
      const { cabin, room } = parseCabinGroup(c.cabin_group);
      return cabin.toLowerCase() === cabinName.toLowerCase() && room.toLowerCase() === roomName.toLowerCase();
    });
  };

  // Evaluate room status/occupancy badge
  const getRoomStatus = (occupants) => {
    if (occupants.length === 0) return { label: "Empty", class: "badge-empty" };
    if (occupants.length === 1) return { label: "Single", class: "badge-single" };

    const firstFamily = occupants[0].family_group;
    const isSingleFamily = firstFamily && occupants.every(o => o.family_group === firstFamily);
    if (isSingleFamily) {
      return { label: `Family (F#${firstFamily})`, class: "badge-family" };
    }
    return { label: "Shared", class: "badge-mixed" };
  };

  // Rooms with zero occupants, across every cabin - used for the "vacant
  // rooms" summary stat and its description list. Grouped by cabin so the
  // panel reads as a scannable list rather than one long flat list.
  const vacantRoomsByCabin = cabinsConfig
    .map(cabin => ({
      cabinName: cabin.name,
      rooms: cabin.rooms
        .map(normalizeRoom)
        .filter(roomObj => getRoomOccupants(cabin.name, roomObj.name).length === 0)
    }))
    .filter(group => group.rooms.length > 0);

  const vacantRoomsCount = vacantRoomsByCabin.reduce((sum, g) => sum + g.rooms.length, 0);

  // Injected CSS Styles for premium look and feel
  const customStyles = `
    .cabins-manager {
      display: grid;
      grid-template-columns: 330px 1fr;
      gap: 24px;
      align-items: start;
    }

    .sidebar-panel {
      background: var(--white);
      border-radius: var(--radius);
      box-shadow: var(--shadow-sm);
      border: 1px solid var(--border);
      display: flex;
      flex-direction: column;
      max-height: calc(100vh - 220px);
      min-height: 400px;
      position: sticky;
      top: 20px;
      overflow: hidden;
    }

    .sidebar-header {
      padding: 16px;
      border-bottom: 1px solid var(--border);
      background: rgba(0, 0, 0, 0.01);
    }

    .sidebar-scrollable {
      flex: 1;
      overflow-y: auto;
      padding: 16px;
      display: flex;
      flex-direction: column;
      gap: 14px;
    }

    .workspace-panel {
      display: flex;
      flex-direction: column;
      gap: 24px;
    }

    .cabin-card {
      background: var(--white);
      border-radius: var(--radius);
      border: 1px solid var(--border);
      box-shadow: var(--shadow-sm);
      overflow: hidden;
    }

    .cabin-header {
      background: var(--forest);
      color: var(--white);
      padding: 12px 20px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-bottom: 2px solid var(--gold);
    }

    .cabin-header h3 {
      font-family: 'Playfair Display', serif;
      margin: 0;
      font-size: 1.15rem;
      font-weight: 600;
    }

    .cabin-rooms-grid {
      padding: 20px;
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
      gap: 20px;
    }

    .room-box {
      border: 1px dashed var(--border);
      border-radius: 12px;
      padding: 16px;
      background: var(--cream);
      display: flex;
      flex-direction: column;
      min-height: 150px;
      transition: all 0.2s ease;
      position: relative;
    }

    .room-box.drag-over {
      border-color: var(--forest);
      background: rgba(47, 82, 51, 0.08);
      transform: scale(1.015);
      box-shadow: 0 4px 12px rgba(47, 82, 51, 0.1);
    }

    .room-box.room-highlighted {
      border: 2px solid var(--gold, #b4975a);
      box-shadow: 0 0 0 4px rgba(180, 151, 90, 0.2);
      animation: room-flash 1.1s ease-in-out 2;
    }

    @keyframes room-flash {
      0%, 100% { background: var(--cream); }
      50% { background: rgba(180, 151, 90, 0.15); }
    }

    .vacant-chip {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      padding: 8px 12px;
      background: var(--white);
      border: 1px solid var(--border);
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.15s ease;
      text-align: left;
      width: 100%;
      font-family: inherit;
    }

    .vacant-chip:hover {
      border-color: var(--gold, #b4975a);
      background: rgba(180, 151, 90, 0.06);
      transform: translateY(-1px);
    }

    .vacant-toggle-btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 4px 10px;
      border-radius: 999px;
      border: 1px solid rgba(37, 99, 235, 0.3);
      background: rgba(37, 99, 235, 0.08);
      color: #1d4ed8;
      font-size: 0.8rem;
      font-weight: 600;
      cursor: pointer;
      font-family: inherit;
      transition: all 0.15s ease;
    }

    .vacant-toggle-btn:hover {
      background: rgba(37, 99, 235, 0.14);
    }

    .room-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;
      border-bottom: 1px solid rgba(0, 0, 0, 0.05);
      padding-bottom: 6px;
    }

    .room-title {
      font-weight: 600;
      font-size: 0.92rem;
      color: var(--forest);
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .room-status-badge {
      font-size: 0.65rem;
      padding: 2px 6px;
      border-radius: 4px;
      font-weight: 600;
    }

    .badge-empty { background: rgba(0,0,0,0.06); color: #4b5563; }
    .badge-single { background: #dbeafe; color: #1e40af; }
    .badge-family { background: #f3e8ff; color: #6b21a8; }
    .badge-mixed { background: #fef3c7; color: #92400e; }

    .occupants-list {
      display: flex;
      flex-direction: column;
      gap: 8px;
      flex: 1;
    }

    .occupant-item {
      background: var(--white);
      border: 1px solid var(--border);
      border-radius: 6px;
      padding: 6px 10px;
      font-size: 0.8rem;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .occupant-info {
      display: flex;
      align-items: center;
      gap: 6px;
      font-weight: 500;
      color: var(--charcoal);
    }

    .family-group-card {
      background: linear-gradient(135deg, #fdfbf7 0%, #f7f1e6 100%);
      border: 1px solid var(--gold-lt);
      border-radius: 10px;
      padding: 14px;
      box-shadow: var(--shadow-sm);
      display: flex;
      flex-direction: column;
      gap: 8px;
      cursor: grab;
      transition: transform 0.2s ease, box-shadow 0.2s ease;
    }

    .family-group-card:hover {
      box-shadow: 0 4px 10px rgba(0,0,0,0.05);
      transform: translateY(-2px);
    }

    .family-group-card.is-dragging-origin,
    .single-camper-card.is-dragging-origin {
      opacity: 0.35;
    }

    .family-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-weight: 600;
      font-size: 0.85rem;
      color: #92400e;
      border-bottom: 1px solid rgba(146, 64, 14, 0.15);
      padding-bottom: 6px;
    }

    .single-camper-card {
      background: var(--white);
      border: 1px solid var(--border);
      border-radius: 10px;
      padding: 14px;
      box-shadow: var(--shadow-sm);
      cursor: grab;
      display: flex;
      flex-direction: column;
      gap: 8px;
      transition: transform 0.2s ease, box-shadow 0.2s ease;
    }

    .single-camper-card:hover {
      box-shadow: 0 4px 10px rgba(0,0,0,0.05);
      transform: translateY(-2px);
    }

    .family-group-card:active,
    .single-camper-card:active {
      cursor: grabbing;
    }

    /* The element that follows the pointer 1:1 while dragging - a compact
       proxy of the real card, not the card itself (the card stays in place,
       dimmed, via .is-dragging-origin above). */
    .drag-ghost {
      background: var(--white);
      border: 1.5px solid var(--forest-mid);
      border-radius: 10px;
      padding: 10px 14px;
      box-shadow: var(--shadow-lg);
      font-size: 0.82rem;
      font-weight: 600;
      color: var(--forest);
      pointer-events: none;
      white-space: nowrap;
    }

    .selector-box {
      font-size: 0.72rem !important;
      padding: 4px 8px !important;
      height: 28px !important;
      width: 100% !important;
      margin-top: 8px !important;
    }

    @media (max-width: 820px) {
      .cabins-manager {
        grid-template-columns: 1fr;
        height: auto;
        min-height: auto;
        gap: 20px;
      }

      .sidebar-panel {
        height: 420px;
      }

      .workspace-panel {
        height: auto;
        overflow-y: visible;
        padding-right: 0;
      }
    }

    .btn-clear-room:hover {
      color: var(--danger, #C0392B) !important;
    }
  `;

  return (
    <>
      <style>{customStyles}</style>

      {dragGhost && (
        <div
          ref={dragGhostRef}
          className="drag-ghost"
          style={{
            position: "fixed",
            left: 0,
            top: 0,
            transform: `translate(${dragGhost.x}px, ${dragGhost.y}px)`,
            zIndex: 2000
          }}
        >
          {dragGhost.label}
        </div>
      )}

      <div className="top-bar">
        <div>
          <h1 style={{ margin: 0 }}>Cabin Room Assigner</h1>
          <div className="text-muted" style={{ fontSize: "0.85rem", display: "flex", gap: 12, alignItems: "center", marginTop: 4, flexWrap: "wrap" }}>
            <span>👤 <strong>{campers.length}</strong> registered</span>
            <span style={{ color: "#ccc" }}>|</span>
            <span style={{ color: "#16a34a" }}>🏠 <strong>{campers.filter(c => c.cabin_group).length}</strong> assigned</span>
            <span style={{ color: "#ccc" }}>|</span>
            <span style={{ color: "#d97706" }}>⚠️ <strong>{campers.filter(c => !c.cabin_group).length}</strong> unassigned</span>
            <span style={{ color: "#ccc" }}>|</span>
            <button
              className="vacant-toggle-btn"
              onClick={() => setShowVacantRooms(v => !v)}
              title="Show/hide the list of completely empty rooms"
            >
              🚪 {vacantRoomsCount} Vacant {vacantRoomsCount === 1 ? "Room" : "Rooms"}
              <span style={{ fontSize: "0.7rem" }}>{showVacantRooms ? "▲" : "▼"}</span>
            </button>
          </div>

          {showVacantRooms && (
            <div style={{
              marginTop: 10, padding: 14, background: "#f7f8f4",
              border: "1px solid var(--border)", borderRadius: 10,
              maxWidth: 480, fontSize: "0.85rem"
            }}>
              {vacantRoomsByCabin.length === 0 ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--muted)" }}>
                  🎉 No completely empty rooms right now — every room has at least one camper assigned.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  {vacantRoomsByCabin.map(group => (
                    <div key={group.cabinName}>
                      <div style={{ fontWeight: 700, color: "var(--forest)", fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>
                        ⛺ {group.cabinName} <span style={{ fontWeight: 400, color: "var(--muted)", textTransform: "none" }}>({group.rooms.length} vacant)</span>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {group.rooms.map(r => (
                          <button
                            key={r.name}
                            className="vacant-chip"
                            onClick={() => jumpToRoom(group.cabinName, r.name)}
                            title="Click to jump to this room"
                          >
                            <span>
                              🚪 <strong>{r.name}</strong>
                              {r.handicap_accessible && <span title="Handicap Accessible" style={{ marginLeft: 6 }}>♿</span>}
                            </span>
                            <span className="text-muted" style={{ fontSize: "0.78rem" }}>{r.location} · capacity {r.max_occupancy}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          {canExport && (
            <button 
              className="btn btn-secondary" 
              onClick={handleExportExcel}
              style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 16px", height: "38px" }}
            >
              📊 Export Excel (CSV)
            </button>
          )}
          {isAdmin && (
            <button 
              className="btn btn-outline" 
              onClick={handlePrintPDF}
              style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 16px", height: "38px" }}
            >
              📄 Print Cabin Details (PDF)
            </button>
          )}
        </div>
      </div>

      <div className="page-body" style={{ display: "flex", flexDirection: "column" }}>
        {error && <div className="alert alert-error">{error}</div>}
        {message && <div className={`alert alert-${message.type}`}>{message.text}</div>}

        {/* Global Toolbar */}
        <div style={{ display: "flex", justifyContent: "space-between", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <div className="search-input-wrap" style={{ width: 260 }}>
              <span className="search-icon">🔍</span>
              <input
                className="form-input"
                placeholder="Search by name or family ID…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ paddingTop: 8, paddingBottom: 8 }}
              />
            </div>
          </div>

          {isAdmin && (
            <form onSubmit={handleAddCabin} style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                className="form-input"
                placeholder="Cabin Name (e.g. Cabin D)…"
                value={newCabinName}
                onChange={e => setNewCabinName(e.target.value)}
                style={{ width: 200, paddingTop: 8, paddingBottom: 8 }}
              />
              <button type="submit" className="btn btn-outline" style={{ padding: "8px 16px", height: "38px" }}>
                ⛺ Add Cabin
              </button>
              {isOwner && (
                <label className="btn btn-secondary" style={{
                  padding: "8px 16px",
                  height: "38px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  cursor: "pointer",
                  margin: 0,
                  fontSize: "0.85rem",
                  fontWeight: 600
                }}>
                  📥 Import Excel
                  <input
                    type="file"
                    accept=".xlsx"
                    onChange={handleImportExcel}
                    style={{ display: "none" }}
                  />
                </label>
              )}
            </form>
          )}
        </div>

        {loading ? (
          <div className="text-center" style={{ padding: 60 }}>
            <div className="spinner" style={{ borderTopColor: "var(--forest-mid)", border: "3px solid #eee", width: 36, height: 36, margin: "0 auto" }} />
          </div>
        ) : (
          <div className="cabins-manager">
            {/* LEFT SIDEBAR: Unassigned list */}
            <div className="sidebar-panel">
              <div className="sidebar-header">
                <h3 style={{ margin: 0, fontSize: "1rem", color: "var(--forest)", fontWeight: 600 }}>
                  Unassigned ({unassignedCampers.length})
                </h3>
                <p className="text-muted" style={{ fontSize: "0.78rem", margin: "4px 0 0 0" }}>
                  {isAdmin ? "Drag cards or use dropdowns to assign" : "Camper registrations waiting for assignments"}
                </p>
              </div>

              <div className="sidebar-scrollable">
                {unassignedList.length === 0 ? (
                  <div style={{ textAlign: "center", color: "var(--muted)", padding: "40px 0", fontSize: "0.82rem" }}>
                    🎉 All registered campers assigned!
                  </div>
                ) : (
                  unassignedList.map(item => {
                    if (item.type === "family") {
                      const ids = item.members.map(m => m.id);
                      return (
                        <div
                          key={`family-${item.id}`}
                          className={`family-group-card ${draggingKey === `family-${item.id}` ? "is-dragging-origin" : ""}`}
                          style={{ touchAction: isAdmin ? "none" : "auto" }}
                          onPointerDown={e => startDrag(e, "family", { ids, familyGroup: item.id }, `family-${item.id}`, `👨‍👩‍👧‍👦 Family #${item.id}`)}
                          onPointerMove={onDragPointerMove}
                          onPointerUp={onDragPointerUp}
                          onPointerCancel={onDragPointerUp}
                        >
                          <div className="family-header">
                            <span>👨‍👩‍👧‍👦 Family #{item.id}</span>
                            <span style={{ fontSize: "0.75rem", fontStyle: "italic" }}>{item.familyName}</span>
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                            {item.members.map(m => (
                              <div key={m.id} style={{ display: "flex", justifyContent: "space-between", fontSize: "0.78rem" }}>
                                <span>
                                  {m.first_name} {m.last_name}
                                </span>
                                <span className="text-muted">
                                  {m.age !== null && m.age !== undefined && m.age !== "" ? "Child" : "Adult"}
                                </span>
                              </div>
                            ))}
                          </div>
                          <CabinRoomSelector camperIds={ids} currentCabin="" currentRoom="" isAdmin={isAdmin} cabinsConfig={cabinsConfig} onAssign={handleSelectorAssign} />
                        </div>
                      );
                    } else {
                      const c = item.camper;
                      return (
                        <div
                          key={`single-${c.id}`}
                          className={`single-camper-card ${draggingKey === `single-${c.id}` ? "is-dragging-origin" : ""}`}
                          style={{ touchAction: isAdmin ? "none" : "auto" }}
                          onPointerDown={e => startDrag(e, "single", { ids: [c.id] }, `single-${c.id}`, `👤 ${c.first_name} ${c.last_name}`)}
                          onPointerMove={onDragPointerMove}
                          onPointerUp={onDragPointerUp}
                          onPointerCancel={onDragPointerUp}
                        >
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                            <span style={{ fontWeight: 600, fontSize: "0.85rem", color: "var(--forest-mid)" }}>
                              👤 {c.first_name} {c.last_name}
                            </span>
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                              <span className="text-muted" style={{ fontSize: "0.75rem" }}>
                                {c.age !== null && c.age !== undefined && c.age !== "" ? "Child" : "Adult"}
                              </span>
                            </div>
                          </div>
                          {c.allergies && (
                            <span style={{ fontSize: "0.72rem", color: "var(--danger)" }}>
                              ⚠️ Allergies: {c.allergies}
                            </span>
                          )}
                          <CabinRoomSelector camperIds={[c.id]} currentCabin="" currentRoom="" isAdmin={isAdmin} cabinsConfig={cabinsConfig} onAssign={handleSelectorAssign} />
                        </div>
                      );
                    }
                  })
                )}
              </div>
            </div>

            {/* RIGHT WORKSPACE: Cabins and rooms */}
            <div className="workspace-panel">
              {cabinsConfig.map(cabin => {
                const cabinCampers = campers.filter(c => {
                  if (!c.cabin_group) return false;
                  const { cabin: cabName } = parseCabinGroup(c.cabin_group);
                  return cabName.toLowerCase() === cabin.name.toLowerCase();
                });
                const cabinOccupancy = cabinCampers.length;

                return (
                  <div key={cabin.name} className="cabin-card">
                    <div className="cabin-header">
                      <div className="cabin-title-area">
                        <span>⛺</span>
                        <h3>{cabin.name}</h3>
                        <span className="badge" style={{ background: "rgba(255,255,255,0.2)", color: "#fff", fontSize: "0.78rem", marginLeft: 12, padding: "2px 8px", borderRadius: 4 }}>
                          Occupants: {cabinOccupancy}
                        </span>
                      </div>
                      
                      {isAdmin && (
                        <div style={{ display: "flex", gap: 10 }}>
                          <button 
                            className="btn btn-gold btn-sm" 
                            style={{ padding: "4px 8px", background: "rgba(255,255,255,0.15)", border: "none", color: "#fff" }}
                            onClick={() => handleAddRoom(cabin.name)}
                          >
                            ➕ Add Room
                          </button>
                          <button 
                            className="btn btn-danger btn-sm" 
                            style={{ padding: "4px 8px" }}
                            onClick={() => handleDeleteCabin(cabin.name)}
                          >
                            Delete Cabin
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="cabin-rooms-grid">
                      {cabin.rooms.length === 0 ? (
                        <div className="text-muted" style={{ padding: 12, fontSize: "0.85rem" }}>
                          No rooms defined. Click "Add Room" to create one.
                        </div>
                      ) : (
                        cabin.rooms.map(room => {
                          const roomObj = normalizeRoom(room);
                          const roomName = roomObj.name;
                          const maxOccupancy = roomObj.max_occupancy;
                          const occupants = getRoomOccupants(cabin.name, roomName);
                          const status = getRoomStatus(occupants);
                          const boxId = `${cabin.name} | ${roomName}`;
                          const isOver = dragOverBox === boxId;
                          const isHighlighted = highlightedBoxId === boxId;

                          return (
                            <div
                              key={roomName}
                              id={roomAnchorId(cabin.name, roomName)}
                              className={`room-box ${isOver ? "drag-over" : ""} ${isHighlighted ? "room-highlighted" : ""}`}
                              data-box-key={boxId}
                              data-cabin-name={cabin.name}
                              data-room-name={roomName}
                            >
                              <div className="room-header">
                                <span className="room-title">
                                  <span>🚪</span> {roomName} <span style={{ fontWeight: 400, fontSize: "0.78rem", color: "var(--muted)", marginLeft: 4 }}>({occupants.length}/{maxOccupancy})</span>
                                </span>
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                  <span className={`room-status-badge ${status.class}`}>
                                    {status.label}
                                  </span>
                                  {isAdmin && (
                                    <button 
                                      onClick={() => setEditingRoom({ cabinName: cabin.name, room: roomObj })}
                                      title="Configure max occupancy and beds"
                                      style={{
                                        border: "none",
                                        background: "transparent",
                                        cursor: "pointer",
                                        fontSize: "0.85rem",
                                        padding: 0,
                                        display: "inline-flex",
                                        alignItems: "center",
                                        color: "var(--muted)",
                                        transition: "color 0.2s"
                                      }}
                                    >
                                      ⚙️
                                    </button>
                                  )}
                                  {isAdmin && occupants.length > 0 && (
                                    <button 
                                      className="btn-clear-room"
                                      onClick={() => clearRoom(cabin.name, roomName)}
                                      title="Unassign all campers from this room"
                                      style={{
                                        border: "none",
                                        background: "transparent",
                                        cursor: "pointer",
                                        fontSize: "0.85rem",
                                        padding: 0,
                                        display: "inline-flex",
                                        alignItems: "center",
                                        color: "var(--muted)",
                                        transition: "color 0.2s"
                                      }}
                                    >
                                      🗑️
                                    </button>
                                  )}
                                </div>
                              </div>

                              {/* Capacity Warning Box */}
                              {occupants.length > maxOccupancy && (
                                <div style={{
                                  backgroundColor: "rgba(220, 53, 69, 0.08)",
                                  border: "1px solid rgba(220, 53, 69, 0.25)",
                                  borderRadius: "6px",
                                  padding: "6px 10px",
                                  marginBottom: 8,
                                  color: "var(--red)",
                                  fontSize: "0.78rem",
                                  fontWeight: 600,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 6
                                }}>
                                  <span>⚠️</span> Over Capacity! Limit: {maxOccupancy}, Assigned: {occupants.length}
                                </div>
                              )}

                              {/* Room Location & Beds Tag */}
                              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
                                <span style={{
                                  fontSize: "0.7rem",
                                  fontWeight: 600,
                                  color: "var(--forest-mid)",
                                  background: "rgba(30,77,43,0.06)",
                                  padding: "2px 6px",
                                  borderRadius: "4px"
                                }}>
                                  📍 {roomObj.location || "Single Level"}
                                </span>
                                
                                {roomObj.handicap_accessible && (
                                  <span style={{
                                    fontSize: "0.7rem",
                                    fontWeight: 700,
                                    color: "#0f52ba",
                                    background: "rgba(15,82,186,0.08)",
                                    padding: "2px 6px",
                                    borderRadius: "4px",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 3
                                  }} title="Handicap Accessible">
                                    ♿ Accessible
                                  </span>
                                )}
                                
                                {Object.entries(roomObj.beds || {}).some(([_, qty]) => qty > 0) && (
                                  <span style={{
                                    fontSize: "0.7rem",
                                    color: "var(--text-secondary)",
                                    background: "#f1f3f5",
                                    padding: "2px 6px",
                                    borderRadius: "4px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 3
                                  }}>
                                    <span>🛏️</span>
                                    <span>
                                      {Object.entries(roomObj.beds)
                                        .filter(([_, qty]) => qty > 0)
                                        .map(([type, qty]) => `${qty}x ${type.charAt(0).toUpperCase() + type.slice(1)}`)
                                        .join(", ")}
                                    </span>
                                  </span>
                                )}
                              </div>

                              <div className="occupants-list">
                                {occupants.length === 0 ? (
                                  <div style={{ display: "flex", flex: 1, alignItems: "center", justifyContent: "center", color: "#aaa", fontSize: "0.75rem", fontStyle: "italic", minHeight: 60 }}>
                                    Vacant
                                  </div>
                                ) : (
                                  occupants.map(occ => (
                                    <div key={occ.id} className="occupant-item">
                                      <div className="occupant-info">
                                        <span>{occ.gender === "male" ? "👦" : occ.gender === "female" ? "👧" : "👤"}</span>
                                        <span title={`${occ.first_name} ${occ.last_name}`}>
                                          {occ.first_name} {occ.last_name.charAt(0)}.
                                        </span>
                                        <span className="text-muted" style={{ fontSize: "0.7rem" }}>
                                          ({occ.age !== null && occ.age !== undefined && occ.age !== "" ? "Child" : "Adult"})
                                        </span>
                                      </div>
                                      
                                      {isAdmin && (
                                        <button 
                                          style={{ border: "none", background: "none", color: "var(--danger)", cursor: "pointer", fontSize: "0.85rem", padding: "0 2px" }}
                                          onClick={() => assignCabin(occ.id, "", "")}
                                          title="Remove from room"
                                        >
                                          ×
                                        </button>
                                      )}
                                    </div>
                                  ))
                                )}
                              </div>

                              {isAdmin && (
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8, borderTop: "1px solid rgba(0,0,0,0.03)", paddingTop: 8 }}>
                                  {occupants.length === 0 && (
                                    <button 
                                      style={{ border: "none", background: "none", color: "var(--muted)", cursor: "pointer", fontSize: "0.75rem" }}
                                      onClick={() => handleDeleteRoom(cabin.name, roomName)}
                                    >
                                      🗑️ Delete Room
                                    </button>
                                  )}
                                  <div style={{ flex: 1 }}>
                                    <CabinRoomSelector
                                      camperIds={occupants.map(o => o.id)}
                                      currentCabin={cabin.name}
                                      currentRoom={roomName}
                                      isAdmin={isAdmin}
                                      cabinsConfig={cabinsConfig}
                                      onAssign={handleSelectorAssign}
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Room Configuration Modal */}
      {editingRoom && (
        <div className="modal-backdrop" style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999,
          backdropFilter: "blur(4px)"
        }}>
          <div className="card" style={{
            width: "100%",
            maxWidth: 450,
            padding: 24,
            background: "#fff",
            borderRadius: "12px",
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
          }}>
            <h3 style={{ marginTop: 0, marginBottom: 16, display: "flex", alignItems: "center", gap: 8, color: "var(--forest-dark)" }}>
              🛏️ Configure {editingRoom.cabinName} — {editingRoom.room.name}
            </h3>
            
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {/* Max Occupancy */}
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <label style={{ fontWeight: 600, fontSize: "0.85rem", color: "var(--text-primary)" }}>Maximum Occupancy</label>
                <input
                  type="number"
                  min="1"
                  className="form-input"
                  value={editingRoom.room.max_occupancy}
                  onChange={e => {
                    const val = e.target.value;
                    setEditingRoom(prev => ({
                      ...prev,
                      room: { ...prev.room, max_occupancy: val }
                    }));
                  }}
                />
              </div>

              {/* Room Location */}
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <label style={{ fontWeight: 600, fontSize: "0.85rem", color: "var(--text-primary)" }}>Room Location</label>
                <select
                  className="form-select"
                  value={editingRoom.room.location || "Single Level"}
                  onChange={e => {
                    const val = e.target.value;
                    setEditingRoom(prev => ({
                      ...prev,
                      room: { ...prev.room, location: val }
                    }));
                  }}
                >
                  <option value="Single Level">Single Level</option>
                  <option value="Upstairs">Upstairs</option>
                  <option value="Downstairs">Downstairs</option>
                </select>
              </div>

              {/* Handicap Accessible */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "8px 0" }}>
                <input
                  type="checkbox"
                  id="edit-handicap-accessible"
                  checked={editingRoom.room.handicap_accessible || false}
                  onChange={e => {
                    const val = e.target.checked;
                    setEditingRoom(prev => ({
                      ...prev,
                      room: { ...prev.room, handicap_accessible: val }
                    }));
                  }}
                  style={{ width: 18, height: 18, cursor: "pointer" }}
                />
                <label htmlFor="edit-handicap-accessible" style={{ fontWeight: 600, fontSize: "0.85rem", color: "var(--text-primary)", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
                  ♿ Handicap Accessible
                </label>
              </div>

              {/* Beds */}
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <label style={{ fontWeight: 600, fontSize: "0.85rem", borderBottom: "1px solid var(--border)", paddingBottom: 6, color: "var(--text-primary)" }}>Beds Configuration</label>
                
                {[
                  { key: "king", label: "👑 King Bed" },
                  { key: "queen", label: "🛏️ Queen Bed" },
                  { key: "full", label: "🛏️ Full Bed" },
                  { key: "twin", label: "🛏️ Twin Bed" },
                  { key: "bunk", label: "🪜 Bunk Bed" }
                ].map(bed => (
                  <div key={bed.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: "0.85rem", color: "var(--text-secondary)" }}>{bed.label}</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        style={{ padding: "2px 8px", minWidth: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center" }}
                        onClick={() => {
                          const current = editingRoom.room.beds[bed.key] || 0;
                          const val = Math.max(0, current - 1);
                          setEditingRoom(prev => ({
                            ...prev,
                            room: {
                              ...prev.room,
                              beds: { ...prev.room.beds, [bed.key]: val }
                            }
                          }));
                        }}
                      >
                        -
                      </button>
                      <span style={{ minWidth: 20, textAlign: "center", fontWeight: 600, fontSize: "0.9rem" }}>
                        {editingRoom.room.beds[bed.key] || 0}
                      </span>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        style={{ padding: "2px 8px", minWidth: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center" }}
                        onClick={() => {
                          const current = editingRoom.room.beds[bed.key] || 0;
                          const val = current + 1;
                          setEditingRoom(prev => ({
                            ...prev,
                            room: {
                              ...prev.room,
                              beds: { ...prev.room.beds, [bed.key]: val }
                            }
                          }));
                        }}
                      >
                        +
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 24 }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setEditingRoom(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  const finalRoom = {
                    ...editingRoom.room,
                    max_occupancy: Math.max(1, parseInt(editingRoom.room.max_occupancy) || 1)
                  };
                  const updated = cabinsConfig.map(cab => {
                    if (cab.name.toLowerCase() === editingRoom.cabinName.toLowerCase()) {
                      return {
                        ...cab,
                        rooms: cab.rooms.map(rm => {
                          const rmName = typeof rm === "string" ? rm : rm.name;
                          if (rmName.toLowerCase() === finalRoom.name.toLowerCase()) {
                            return finalRoom;
                          }
                          return rm;
                        })
                      };
                    }
                    return cab;
                  });
                  saveConfig(updated);
                  setEditingRoom(null);
                  flash("success", `Configured ${editingRoom.cabinName} — ${finalRoom.name} successfully.`);
                }}
              >
                Save Configuration
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Room Modal */}
      {addingRoomToCabin && (
        <div className="modal-backdrop" style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999,
          backdropFilter: "blur(4px)"
        }}>
          <div className="card" style={{
            width: "100%",
            maxWidth: 450,
            padding: 24,
            background: "#fff",
            borderRadius: "12px",
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
          }}>
            <h3 style={{ marginTop: 0, marginBottom: 16, display: "flex", alignItems: "center", gap: 8, color: "var(--forest-dark)" }}>
              ➕ Add New Room to {addingRoomToCabin}
            </h3>
            
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {/* Room Name */}
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <label style={{ fontWeight: 600, fontSize: "0.85rem", color: "var(--text-primary)" }}>Room Number or Name</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g. Room 101"
                  value={newRoomForm.name}
                  onChange={e => setNewRoomForm(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>

              {/* Max Occupancy */}
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <label style={{ fontWeight: 600, fontSize: "0.85rem", color: "var(--text-primary)" }}>Maximum Occupancy</label>
                <input
                  type="number"
                  min="1"
                  className="form-input"
                  value={newRoomForm.max_occupancy}
                  onChange={e => setNewRoomForm(prev => ({ ...prev, max_occupancy: e.target.value }))}
                />
              </div>

              {/* Room Location */}
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <label style={{ fontWeight: 600, fontSize: "0.85rem", color: "var(--text-primary)" }}>Room Location</label>
                <select
                  className="form-select"
                  value={newRoomForm.location || "Single Level"}
                  onChange={e => {
                    const val = e.target.value;
                    setNewRoomForm(prev => ({
                      ...prev,
                      location: val
                    }));
                  }}
                >
                  <option value="Single Level">Single Level</option>
                  <option value="Upstairs">Upstairs</option>
                  <option value="Downstairs">Downstairs</option>
                </select>
              </div>

              {/* Handicap Accessible */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "8px 0" }}>
                <input
                  type="checkbox"
                  id="add-handicap-accessible"
                  checked={newRoomForm.handicap_accessible || false}
                  onChange={e => {
                    const val = e.target.checked;
                    setNewRoomForm(prev => ({
                      ...prev,
                      handicap_accessible: val
                    }));
                  }}
                  style={{ width: 18, height: 18, cursor: "pointer" }}
                />
                <label htmlFor="add-handicap-accessible" style={{ fontWeight: 600, fontSize: "0.85rem", color: "var(--text-primary)", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
                  ♿ Handicap Accessible
                </label>
              </div>

              {/* Beds */}
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <label style={{ fontWeight: 600, fontSize: "0.85rem", borderBottom: "1px solid var(--border)", paddingBottom: 6, color: "var(--text-primary)" }}>Beds Configuration</label>
                
                {[
                  { key: "king", label: "👑 King Bed" },
                  { key: "queen", label: "🛏️ Queen Bed" },
                  { key: "full", label: "🛏️ Full Bed" },
                  { key: "twin", label: "🛏️ Twin Bed" },
                  { key: "bunk", label: "🪜 Bunk Bed" }
                ].map(bed => (
                  <div key={bed.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: "0.85rem", color: "var(--text-secondary)" }}>{bed.label}</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        style={{ padding: "2px 8px", minWidth: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center" }}
                        onClick={() => {
                          const current = newRoomForm.beds[bed.key] || 0;
                          const val = Math.max(0, current - 1);
                          setNewRoomForm(prev => ({
                            ...prev,
                            beds: { ...prev.beds, [bed.key]: val }
                          }));
                        }}
                      >
                        -
                      </button>
                      <span style={{ minWidth: 20, textAlign: "center", fontWeight: 600, fontSize: "0.9rem" }}>
                        {newRoomForm.beds[bed.key] || 0}
                      </span>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        style={{ padding: "2px 8px", minWidth: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center" }}
                        onClick={() => {
                          const current = newRoomForm.beds[bed.key] || 0;
                          const val = current + 1;
                          setNewRoomForm(prev => ({
                            ...prev,
                            beds: { ...prev.beds, [bed.key]: val }
                          }));
                        }}
                      >
                        +
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 24 }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setAddingRoomToCabin(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={async () => {
                  const nameTrimmed = newRoomForm.name.trim();
                  if (!nameTrimmed) {
                    await alert("Room number or name is required.");
                    return;
                  }
                  
                  // Check duplicate
                  let hasDuplicate = false;
                  const newConfig = cabinsConfig.map(c => {
                    if (c.name.toLowerCase() === addingRoomToCabin.toLowerCase()) {
                      const roomNames = c.rooms.map(r => typeof r === "string" ? r.toLowerCase() : r.name.toLowerCase());
                      if (roomNames.includes(nameTrimmed.toLowerCase())) {
                        hasDuplicate = true;
                        return c;
                      }
                      const finalRoom = {
                        name: nameTrimmed,
                        max_occupancy: Math.max(1, parseInt(newRoomForm.max_occupancy) || 1),
                        location: newRoomForm.location || "Single Level",
                        handicap_accessible: newRoomForm.handicap_accessible || false,
                        beds: {
                          king: parseInt(newRoomForm.beds.king) || 0,
                          queen: parseInt(newRoomForm.beds.queen) || 0,
                          full: parseInt(newRoomForm.beds.full) || 0,
                          twin: parseInt(newRoomForm.beds.twin) || 0,
                          bunk: parseInt(newRoomForm.beds.bunk) || 0
                        }
                      };
                      const updatedRooms = [...c.rooms, finalRoom].sort((a, b) => 
                        a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' })
                      );
                      return { ...c, rooms: updatedRooms };
                    }
                    return c;
                  });

                  if (hasDuplicate) {
                    await alert("Room name already exists inside this cabin.");
                    return;
                  }

                  saveConfig(newConfig);
                  setAddingRoomToCabin(null);
                  flash("success", `Added "${nameTrimmed}" to ${addingRoomToCabin}.`);
                }}
              >
                Add Room
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
